import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X, ShieldCheck, Lock } from "lucide-react";
import {
    COOKIE_CATEGORIES,
    getConsent,
    setConsent,
} from "../lib/cookieConsent";
import { useLang } from "../lib/i18n";

/**
 * GDPR-compliant cookie consent banner.
 * - Shown only if no valid consent record exists for the current policy version.
 * - No pre-ticked optional boxes.
 * - Equal-prominence Accept All / Reject All / Manage Preferences buttons.
 * - Manage Preferences opens a modal with independent toggles per category.
 * - Listens to "elh-consent-open" events so the footer link can re-open it.
 */
export default function CookieBanner() {
    const { T } = useLang();
    const [open, setOpen] = useState(() => !getConsent());
    const [manage, setManage] = useState(false);
    const initialChoices = useInitialChoices();
    const [choices, setChoices] = useState(initialChoices);

    useEffect(() => {
        const onOpen = () => {
            setChoices(getConsent()?.categories || baseline());
            setManage(true);
            setOpen(true);
        };
        const onChange = () => {
            setOpen(!getConsent());
        };
        window.addEventListener("elh-consent-open", onOpen);
        window.addEventListener("elh-consent-change", onChange);
        return () => {
            window.removeEventListener("elh-consent-open", onOpen);
            window.removeEventListener("elh-consent-change", onChange);
        };
    }, []);

    if (!open) return null;

    const acceptAll = () => {
        setConsent({
            action: "accept_all",
            categories: { necessary: true, preferences: true, analytics: true, marketing: true },
        });
        setOpen(false);
        setManage(false);
    };
    const rejectAll = () => {
        setConsent({
            action: "reject_all",
            categories: { necessary: true, preferences: false, analytics: false, marketing: false },
        });
        setOpen(false);
        setManage(false);
    };
    const saveGranular = () => {
        setConsent({
            action: "granular",
            categories: { ...choices, necessary: true },
        });
        setOpen(false);
        setManage(false);
    };

    return (
        <>
            {/* Inline bottom banner */}
            {!manage && (
                <div
                    role="dialog"
                    aria-label="Cookie consent"
                    aria-live="polite"
                    className="fixed bottom-0 left-0 right-0 z-[60] bg-[#0D1B3E] text-[#F5F0E8] border-t-2 border-[#C9A84C] shadow-2xl"
                    data-testid="cookie-banner"
                >
                    <div className="container-edit py-3 sm:py-5 lg:py-6 flex flex-col lg:flex-row items-start lg:items-center gap-3 lg:gap-6">
                        <div className="flex items-start gap-3 flex-1 min-w-0">
                            <Cookie size={22} color="#C9A84C" className="shrink-0 mt-0.5" />
                            <div className="flex-1 min-w-0">
                                <div className="label-eyebrow hidden sm:block" style={{ color: "#C9A84C" }}>
                                    {T("Cookies", "Çerezler")}
                                </div>
                                <p className="sm:hidden text-[13px] leading-snug text-[#F5F0E8]/90">
                                    {T("We use necessary cookies; optional ones only with your", "Zorunlu çerezler kullanırız; isteğe bağlı çerezler yalnızca sizin")}{" "}
                                    <Link to="/privacy" className="underline">{T("consent", "onayınızla")}</Link>.
                                </p>
                                <p className="hidden sm:block mt-1 text-sm leading-relaxed text-[#F5F0E8]/90">
                                    {T(
                                        "We use strictly-necessary cookies to keep EuroLaw Hub secure and functional. With your consent we also use optional cookies for preferences, analytics and marketing. You can accept all, reject all, or choose what to allow. Your choice is recorded for",
                                        "EuroLaw Hub'ı güvenli ve işlevsel tutmak için kesinlikle gerekli çerezleri kullanırız. Onayınızla, tercihler, analitik ve pazarlama için isteğe bağlı çerezler de kullanırız. Tümünü kabul edebilir, tümünü reddedebilir veya neye izin vereceğinizi seçebilirsiniz. Tercihiniz"
                                    )}{" "}
                                    <Link to="/privacy" className="underline">
                                        {T("accountability", "hesap verebilirlik")}
                                    </Link>{" "}
                                    {T("and you can change it any time from the footer.", "amacıyla kaydedilir ve alt menüden dilediğiniz zaman değiştirilebilir.")}
                                </p>
                            </div>
                        </div>
                        <div className="flex flex-wrap gap-2 items-center w-full lg:w-auto" data-testid="cookie-banner-actions">
                            <button
                                type="button"
                                onClick={() => setManage(true)}
                                className="min-h-[44px] bg-transparent border border-[#F5F0E8]/40 text-[#F5F0E8] hover:border-[#C9A84C] hover:text-[#C9A84C] px-3 sm:px-4 py-2 text-xs uppercase tracking-[0.18em] transition-colors"
                                data-testid="cookie-banner-manage"
                            >
                                {T("Manage", "Yönet")}
                            </button>
                            <button
                                type="button"
                                onClick={rejectAll}
                                className="min-h-[44px] bg-transparent border border-[#F5F0E8]/40 text-[#F5F0E8] hover:border-[#C9A84C] hover:text-[#C9A84C] px-3 sm:px-4 py-2 text-xs uppercase tracking-[0.18em] transition-colors"
                                data-testid="cookie-banner-reject"
                            >
                                {T("Reject all", "Tümünü reddet")}
                            </button>
                            <button
                                type="button"
                                onClick={acceptAll}
                                className="min-h-[44px] bg-[#C9A84C] text-[#0D1B3E] hover:bg-[#d9b85c] px-3 sm:px-4 py-2 text-xs uppercase tracking-[0.18em] font-semibold transition-colors"
                                data-testid="cookie-banner-accept"
                            >
                                {T("Accept all", "Tümünü kabul et")}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Manage modal */}
            {manage && (
                <div
                    className="fixed inset-0 z-[70] bg-[#0D1B3E]/70 grid place-items-center p-4"
                    role="dialog"
                    aria-label="Cookie preferences"
                    data-testid="cookie-modal-overlay"
                >
                    <div
                        className="bg-white border border-[#0D1B3E]/20 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
                        data-testid="cookie-modal"
                    >
                        <header className="px-6 py-5 border-b border-[#0D1B3E]/10 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <ShieldCheck size={18} color="#C9A84C" />
                                <div>
                                    <div className="label-eyebrow">{T("Cookie preferences", "Çerez tercihleri")}</div>
                                    <div className="font-display text-xl tracking-tight">
                                        {T("Choose what you allow", "Nelere izin verdiğinizi seçin")}
                                    </div>
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={() => {
                                    setManage(false);
                                    if (!getConsent()) setOpen(true);
                                }}
                                className="text-[#0D1B3E]/60 hover:text-[#0D1B3E]"
                                aria-label="Close"
                                data-testid="cookie-modal-close"
                            >
                                <X size={22} />
                            </button>
                        </header>

                        <div className="px-6 py-6 space-y-4" data-testid="cookie-modal-categories">
                            {COOKIE_CATEGORIES.map((cat) => {
                                const enabled = cat.locked ? true : !!choices[cat.key];
                                return (
                                    <div
                                        key={cat.key}
                                        className="card-flat p-5"
                                        data-testid={`cookie-cat-${cat.key}`}
                                    >
                                        <div className="flex items-start gap-4">
                                            <div className="min-w-0 flex-1">
                                                <div className="flex items-center gap-2 label-eyebrow">
                                                    <span style={{ color: "#C9A84C" }}>
                                                        {cat.label}
                                                    </span>
                                                    {cat.locked && (
                                                        <span className="flex items-center gap-1 text-[#0D1B3E]/55">
                                                            <Lock size={11} /> Always on
                                                        </span>
                                                    )}
                                                </div>
                                                <p className="mt-2 text-sm text-[#0D1B3E]/85 leading-relaxed">
                                                    {cat.description}
                                                </p>
                                                <p className="mt-2 text-xs text-[#0D1B3E]/55">
                                                    Legal basis: {cat.legal_basis}
                                                </p>
                                            </div>
                                            <Toggle
                                                checked={enabled}
                                                disabled={cat.locked}
                                                onChange={(v) =>
                                                    setChoices((c) => ({ ...c, [cat.key]: v }))
                                                }
                                                testId={`cookie-toggle-${cat.key}`}
                                            />
                                        </div>
                                        {cat.examples?.length > 0 && (
                                            <details className="mt-3 text-xs text-[#0D1B3E]/70">
                                                <summary className="cursor-pointer hover:text-[#C9A84C]">
                                                    What we store in this category
                                                </summary>
                                                <ul className="mt-2 space-y-1 pl-2">
                                                    {cat.examples.map((e, i) => (
                                                        <li key={i} className="flex gap-2">
                                                            <span>–</span>
                                                            <span>{e}</span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </details>
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        <footer className="px-6 py-5 border-t border-[#0D1B3E]/10 flex flex-wrap items-center gap-3 justify-between">
                            <div className="text-xs text-[#0D1B3E]/60">
                                See the full{" "}
                                <Link to="/privacy" className="underline" onClick={() => { setManage(false); setOpen(false); }}>
                                    Privacy Policy
                                </Link>
                                .
                            </div>
                            <div className="flex flex-wrap gap-2">
                                <button
                                    type="button"
                                    onClick={rejectAll}
                                    className="btn-secondary"
                                    data-testid="cookie-modal-reject"
                                >
                                    {T("Reject all", "Tümünü reddet")}
                                </button>
                                <button
                                    type="button"
                                    onClick={saveGranular}
                                    className="btn-secondary"
                                    data-testid="cookie-modal-save"
                                >
                                    {T("Save preferences", "Tercihleri kaydet")}
                                </button>
                                <button
                                    type="button"
                                    onClick={acceptAll}
                                    className="btn-primary"
                                    data-testid="cookie-modal-accept"
                                >
                                    {T("Accept all", "Tümünü kabul et")}
                                </button>
                            </div>
                        </footer>
                    </div>
                </div>
            )}
        </>
    );
}

function Toggle({ checked, disabled, onChange, testId }) {
    return (
        <button
            type="button"
            role="switch"
            aria-checked={checked}
            aria-disabled={disabled}
            disabled={disabled}
            onClick={() => onChange(!checked)}
            data-testid={testId}
            className={`relative shrink-0 inline-flex h-7 w-12 items-center rounded-full transition-colors ${
                checked ? "bg-[#C9A84C]" : "bg-[#0D1B3E]/20"
            } ${disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
        >
            <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                    checked ? "translate-x-6" : "translate-x-1"
                }`}
            />
        </button>
    );
}

function baseline() {
    return { necessary: true, preferences: false, analytics: false, marketing: false };
}

function useInitialChoices() {
    return getConsent()?.categories || baseline();
}
